import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, RefreshCw, LogOut } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { useAccessResolver } from "@/hooks/useAccessResolver";
import { routeFromAccess } from "@/lib/routeFromAccess";
import { AccessErrorScreen } from "@/components/AccessErrorScreen";
import { useQueryClient } from "@tanstack/react-query";
import { useProperties } from "@/hooks/useProperties";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { getInviteToken } from "@/lib/inviteTokenStorage";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, profile, loading, signOut, refetchProfile } = useAuth();
  const { data: access, isLoading: accessLoading } = useAccessResolver();
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const hasRedirected = useRef(false);

  // Cleaners are not required to verify email; redirect so they never see this page
  useEffect(() => {
    if (loading || !user || !profile || hasRedirected.current) return;
    if (profile.role !== "cleaner") return;
    hasRedirected.current = true;
    const inviteToken = getInviteToken();
    if (inviteToken) {
      navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
    } else {
      navigate("/worker/schedule", { replace: true });
    }
  }, [loading, user, profile, navigate]);
  
  // Phase 3A: Check for first-time user status (properties and assigned tasks)
  const { data: properties, isLoading: propertiesLoading } = useProperties();
  const { data: cleaner, isLoading: cleanerLoading } = useCleanerRecord();

  // Phase 3A: Check if user has assigned tasks (for first-time user detection)
  const [hasAssignedTasks, setHasAssignedTasks] = useState<boolean | null>(null);
  const [isCheckingTasks, setIsCheckingTasks] = useState(false);

  // Check for assigned tasks when cleaner record is available
  useEffect(() => {
    const checkAssignedTasks = async () => {
      if (!cleaner || cleanerLoading || isCheckingTasks) return;
      
      setIsCheckingTasks(true);
      try {
        const { data: tasks, error } = await supabase
          .from("cleaning_tasks")
          .select("id")
          .eq("cleaner_id", cleaner.id)
          .limit(1);
        
        if (error) {
          console.error("[VerifyEmail] Error checking assigned tasks:", error);
          setHasAssignedTasks(false);
        } else {
          setHasAssignedTasks((tasks?.length ?? 0) > 0);
        }
      } catch (error) {
        console.error("[VerifyEmail] Error checking assigned tasks:", error);
        setHasAssignedTasks(false);
      } finally {
        setIsCheckingTasks(false);
      }
    };

    if (cleaner && !cleanerLoading) {
      checkAssignedTasks();
    } else if (!cleaner && !cleanerLoading) {
      // No cleaner record means no assigned tasks
      setHasAssignedTasks(false);
    }
  }, [cleaner, cleanerLoading, isCheckingTasks]);

  // Auto-redirect if email is verified and profile is loaded - USING ACCESS RESOLVER (no role-based routing)
  useEffect(() => {
    // Only redirect if email is confirmed AND profile email_verified is true
    if (!loading && !accessLoading && user && profile && user.email_confirmed_at && profile.email_verified && !hasRedirected.current) {
      console.log("Auto-redirect triggered - email verified, profile loaded");
      
      // If user came from an invite link, return to /invite to complete acceptance and land on the right dashboard
      const inviteToken = getInviteToken();
      if (inviteToken) {
        hasRedirected.current = true;
        navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
        return;
      }
      
      // Use access resolver to determine route (SINGLE SOURCE OF TRUTH)
      const redirectPath = routeFromAccess(access, accessLoading);
      
      if (redirectPath) {
        hasRedirected.current = true;
        console.log("Auto-redirecting to:", redirectPath);
        navigate(redirectPath, { replace: true });
      } else if (access?.type === "no_access") {
        // Phase 3A: Check if this is a first-time user (no properties, no tasks, no invite)
        const isLoadingFirstTimeCheck = propertiesLoading || cleanerLoading || isCheckingTasks || hasAssignedTasks === null;
        
        if (!isLoadingFirstTimeCheck) {
          const hasProperties = (properties?.length ?? 0) > 0;
          const hasTasks = hasAssignedTasks === true;
          if (!hasProperties && !hasTasks) {
            hasRedirected.current = true;
            if (profile?.role === "owner") {
              navigate("/owner/properties", { replace: true });
            } else if (profile?.role === "pending_invite") {
              // Invited users: complete acceptance via /invite; do not send to Create property onboarding
              const token = getInviteToken();
              navigate(token ? `/invite?token=${encodeURIComponent(token)}` : "/invite", { replace: true });
            } else if (profile?.role === "company_admin") {
              navigate("/owner/properties", { replace: true });
            } else if (profile?.role === "cleaner") {
              navigate("/worker/schedule", { replace: true });
            } else {
              navigate("/onboarding/decision", { replace: true });
            }
            return;
          }
        }
        
        // Access cannot be resolved and user has resources - don't redirect, will show error screen
        console.warn("Access cannot be resolved after email verification");
      }
    } else if (!loading && user && profile && user.email_confirmed_at && !profile.email_verified && !hasRedirected.current) {
      // Email is confirmed but profile email_verified is false - sync it
      console.log("Email confirmed but profile email_verified is false, syncing...");
      supabase
        .rpc("sync_email_verified", { user_id: user.id })
        .then(({ error }) => {
          if (error) {
            console.error("Error syncing email_verified:", error);
            // Fallback to direct update
            supabase
              .from("profiles")
              .update({ email_verified: true })
              .eq("id", user.id);
          }
          refetchProfile();
        });
    }
  }, [user, profile, loading, access, accessLoading, navigate, refetchProfile, properties, propertiesLoading, cleaner, cleanerLoading, hasAssignedTasks, isCheckingTasks]);

  const handleResendEmail = async () => {
    if (!user?.email) return;
    
    setIsResending(true);
    const redirectTo = hasInviteToken
      ? `${window.location.origin}/invite/complete`
      : `${window.location.origin}/`;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: user.email,
      options: {
        emailRedirectTo: redirectTo,
      },
    });

    if (error) {
      toast({
        title: "Failed to resend",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Email sent",
        description: "Check your inbox for the verification link.",
      });
    }
    setIsResending(false);
  };

  const handleCheckVerification = async () => {
    setIsChecking(true);
    
    // First check if we have a session
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession) {
      toast({
        title: "Error checking verification",
        description: "No active session. Please sign in again.",
        variant: "destructive",
      });
      setIsChecking(false);
      return;
    }
    
    // Refresh the session to get updated email_confirmed_at
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      toast({
        title: "Error checking verification",
        description: error.message,
        variant: "destructive",
      });
      setIsChecking(false);
      return;
    }

    if (session?.user?.email_confirmed_at) {
      console.log("Email verified! Fetching profile for redirect...");
      
      // Use the sync function to ensure email_verified is set correctly
      const { error: syncError } = await supabase.rpc("sync_email_verified", {
        user_id: session.user.id
      });
      
      if (syncError) {
        console.error("Error syncing email_verified via RPC, trying direct update:", syncError);
        // Fallback to direct update if RPC fails
        const { error: updateError } = await supabase
          .from("profiles")
          .update({ email_verified: true })
          .eq("id", session.user.id);
        
        if (updateError) {
          console.error("Error updating email_verified directly:", updateError);
        }
      }
      
      // Refetch profile to get updated data
      await refetchProfile();
      
      // Wait a moment for the profile state to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Verify email_verified is true before redirecting
      const { data: profileCheck } = await supabase
        .from("profiles")
        .select("email_verified")
        .eq("id", session.user.id)
        .maybeSingle();
      
      if (!profileCheck?.email_verified) {
        console.warn("Profile email_verified is still false, updating again...");
        await supabase
          .from("profiles")
          .update({ email_verified: true })
          .eq("id", session.user.id);
        await new Promise(resolve => setTimeout(resolve, 300));
        await refetchProfile();
      }
      
      // Invalidate access resolver query to force refetch with updated profile
      queryClient.invalidateQueries({ queryKey: ["access-resolver"] });
      
      // Prevent multiple redirects
      if (hasRedirected.current) {
        setIsChecking(false);
        return;
      }
      hasRedirected.current = true;
      
      toast({
        title: "Email verified!",
        description: "Redirecting...",
      });
      
      // If user came from an invite link, return them to /invite to complete acceptance and land on the right dashboard
      const inviteToken = getInviteToken();
      if (inviteToken) {
        navigate(`/invite?token=${encodeURIComponent(inviteToken)}`, { replace: true });
        return;
      }
      navigate("/login", { replace: true });
      
      return; // Don't set isChecking to false, we're redirecting
    } else {
      toast({
        title: "Not yet verified",
        description: "Please click the link in your email to verify.",
        variant: "destructive",
      });
    }
    setIsChecking(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const hasInviteToken = !!getInviteToken();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>
            {hasInviteToken ? "Confirm your email to join" : "Verify Your Email"}
          </CardTitle>
          <CardDescription>
            {hasInviteToken
              ? "Once confirmed, you'll be taken directly into the property."
              : <>We've sent a verification link to <strong>{user?.email}</strong></>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            {hasInviteToken
              ? "Check your inbox and click the verification link. If you don't see the email, check your spam folder."
              : "Please check your inbox and click the verification link to access your dashboard. If you don't see the email, check your spam folder."}
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={handleCheckVerification} 
              className="w-full"
              disabled={isChecking}
            >
              {isChecking ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                "I've Verified My Email"
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleResendEmail} 
              className="w-full"
              disabled={isResending}
            >
              {isResending ? "Sending..." : "Resend Verification Email"}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleLogout} 
              className="w-full text-muted-foreground"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
